/**
 * Image Upload Component
 * ======================
 * Upload images by URL or file upload.
 */

import { useState, useRef, useCallback } from 'react'
import { Upload, Link, X, Check } from 'lucide-react'
import { clsx } from 'clsx'
import Button from '@/components/Button'
import Input from '@/components/Input'
import { useUploadImage, useAddImageByUrl } from '@/hooks/useProductImages'

interface ImageUploadProps {
  productId: number | string
  onImageAdded?: () => void
  disabled?: boolean
}

type UploadMode = 'file' | 'url'

const MAX_FILE_SIZE_MB = 20
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

export default function ImageUpload({ productId, onImageAdded, disabled }: ImageUploadProps) {
  const [mode, setMode] = useState<UploadMode>('file')
  const [url, setUrl] = useState('')
  const [isPrimary, setIsPrimary] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const uploadImage = useUploadImage()
  const addImageByUrl = useAddImageByUrl()

  const isLoading = uploadImage.isPending || addImageByUrl.isPending

  const validateFile = useCallback((file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Allowed: JPEG, PNG, GIF, WebP'
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File too large. Maximum size: ${MAX_FILE_SIZE_MB}MB`
    }
    return null
  }, [])

  const handleFileSelect = useCallback(
    (file: File) => {
      const validationError = validateFile(file)
      if (validationError) {
        setError(validationError)
        return
      }

      setError(null)
      setSelectedFile(file)

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setPreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    },
    [validateFile]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragActive(false)

      const file = e.dataTransfer.files[0]
      if (file) {
        handleFileSelect(file)
      }
    },
    [handleFileSelect]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
  }, [])

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        handleFileSelect(file)
      }
    },
    [handleFileSelect]
  )

  const clearSelection = useCallback(() => {
    setSelectedFile(null)
    setPreview(null)
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleUpload = async () => {
    setError(null)

    try {
      if (mode === 'file' && selectedFile) {
        await uploadImage.mutateAsync({
          productId,
          file: selectedFile,
          isPrimary,
        })
        clearSelection()
      } else if (mode === 'url' && url.trim()) {
        await addImageByUrl.mutateAsync({
          productId,
          data: {
            image_url: url.trim(),
            is_primary: isPrimary,
            source: 'uploaded',
          },
        })
        setUrl('')
      }

      setIsPrimary(false)
      onImageAdded?.()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed'
      setError(message)
    }
  }

  const canUpload =
    (mode === 'file' && selectedFile) || (mode === 'url' && url.trim().length > 0)

  return (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode('file')}
          className={clsx(
            'flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors',
            mode === 'file'
              ? 'bg-primary-50 border-primary-500 text-primary-700'
              : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
          )}
        >
          <Upload size={16} />
          Upload File
        </button>
        <button
          type="button"
          onClick={() => setMode('url')}
          className={clsx(
            'flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors',
            mode === 'url'
              ? 'bg-primary-50 border-primary-500 text-primary-700'
              : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
          )}
        >
          <Link size={16} />
          Image URL
        </button>
      </div>

      {/* File Upload Mode */}
      {mode === 'file' && (
        <div>
          {!selectedFile ? (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={clsx(
                'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                dragActive
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">
                Drag and drop an image, or <span className="text-primary-600">browse</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                JPEG, PNG, GIF, WebP (max {MAX_FILE_SIZE_MB}MB)
              </p>
            </div>
          ) : (
            <div className="relative border rounded-lg p-3 bg-gray-50">
              <button
                type="button"
                onClick={clearSelection}
                className="absolute top-2 right-2 p-1 bg-white rounded-full shadow hover:bg-gray-100"
              >
                <X size={14} />
              </button>
              <div className="flex items-center gap-3">
                {preview && (
                  <img
                    src={preview}
                    alt="Preview"
                    className="h-16 w-16 object-cover rounded"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Check className="h-5 w-5 text-green-500" />
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_TYPES.join(',')}
            onChange={handleFileInputChange}
            className="hidden"
            disabled={disabled}
          />
        </div>
      )}

      {/* URL Mode */}
      {mode === 'url' && (
        <Input
          label="Image URL"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com/image.jpg"
          disabled={disabled}
        />
      )}

      {/* Primary Checkbox */}
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={isPrimary}
          onChange={(e) => setIsPrimary(e.target.checked)}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          disabled={disabled}
        />
        <span className="text-sm text-gray-700">Set as primary image</span>
      </label>

      {/* Error Message */}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Upload Button */}
      <Button
        type="button"
        onClick={handleUpload}
        disabled={disabled || !canUpload || isLoading}
        loading={isLoading}
        className="w-full"
        icon={<Upload size={16} />}
      >
        {mode === 'file' ? 'Upload Image' : 'Add Image'}
      </Button>
    </div>
  )
}
